import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { MODULES, type Module } from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import {
  useNotifications,
  type NotificationItem,
  type NotificationPriority,
} from '../contexts/NotificationsContext';
import {
  getNotificationTypeStyle,
  getPriorityBadgeClass,
  getPriorityLabel,
  relativeTime,
} from '../lib/notification-style';

/**
 * Page /notifications — US-NOTIF-03 (centre complet).
 *
 *  - KPIs : total / non lues / aujourd'hui / urgentes
 *  - Filtres : statut, module, priorité, plage dates, recherche libre
 *  - Tableau paginé avec actions par ligne + actions globales
 *  - Clic ligne → marquer lue + navigation vers `action_url`
 */

interface Stats {
  total: number;
  unread: number;
  today: number;
  urgent: number;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

type StatusFilter = 'all' | 'unread' | 'read';
type PriorityFilter = NotificationPriority | '';

export function NotificationsPage() {
  const navigate = useNavigate();
  const { markRead, markUnread, markAllRead, dismiss, refresh } = useNotifications();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [module, setModule] = useState<Module | ''>('');
  const [priority, setPriority] = useState<PriorityFilter>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [status, module, priority, debouncedSearch, from, to]);

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, module, priority, debouncedSearch, from, to, page]);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadPage() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      params.set('status', status);
      if (module) params.set('module', module);
      if (priority) params.set('priority', priority);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (from) params.set('from', new Date(from).toISOString());
      if (to) params.set('to', new Date(to).toISOString());
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await api.get<{ data: NotificationItem[]; pagination: Pagination }>(
        `/notifications?${params}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await api.get<{ data: Stats }>('/notifications/stats');
      setStats(res.data.data);
    } catch {
      // Silencieux.
    }
  }

  async function refreshAll() {
    await Promise.all([loadPage(), loadStats(), refresh()]);
  }

  function resetFilters() {
    setStatus('all');
    setModule('');
    setPriority('');
    setSearch('');
    setFrom('');
    setTo('');
  }

  async function handleClick(n: NotificationItem) {
    if (!n.is_read) {
      await markRead(n.id);
    }
    if (n.action_url) {
      navigate(n.action_url);
    }
    loadStats();
  }

  async function handleMarkAllRead() {
    await markAllRead();
    refreshAll();
  }

  async function handlePurgeRead() {
    if (!confirm('Supprimer définitivement toutes les notifications lues ?')) return;
    try {
      await api.delete('/notifications/read');
      refreshAll();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <Bell className="w-7 h-7 mr-2 text-primary-600" />
        Notifications
      </h1>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total" value={stats?.total ?? 0} tone="default" />
        <KpiCard label="Non lues" value={stats?.unread ?? 0} tone="primary" />
        <KpiCard label="Aujourd'hui" value={stats?.today ?? 0} tone="blue" />
        <KpiCard label="Urgentes" value={stats?.urgent ?? 0} tone="red" />
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 border-b border-gray-200">
          <div className="relative col-span-1 sm:col-span-2 lg:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Rechercher (titre, message)…"
              className={inputClass + ' pl-10'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            className={inputClass}
          >
            <option value="all">Tous statuts</option>
            <option value="unread">Non lues</option>
            <option value="read">Lues</option>
          </select>
          <select
            value={module}
            onChange={(e) => setModule(e.target.value as Module | '')}
            className={inputClass}
          >
            <option value="">Tous modules</option>
            {MODULES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as PriorityFilter)}
            className={inputClass}
          >
            <option value="">Toutes priorités</option>
            <option value="normal">Normal</option>
            <option value="high">Haute</option>
            <option value="urgent">Urgente</option>
          </select>
          <button
            onClick={resetFilters}
            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            title="Réinitialiser les filtres"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Réinitialiser
          </button>
          <div className="col-span-1 sm:col-span-2 lg:col-span-3">
            <label className="text-xs text-gray-500 block mb-1">Depuis</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="col-span-1 sm:col-span-2 lg:col-span-3">
            <label className="text-xs text-gray-500 block mb-1">Jusqu'à</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Actions globales */}
        <div className="px-4 py-2 flex items-center justify-between text-sm bg-gray-50 border-b border-gray-200">
          <p className="text-gray-600">
            {pagination ? `${pagination.total} notification(s)` : '…'}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={refreshAll}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Actualiser
            </button>
            <button
              onClick={handleMarkAllRead}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-primary-200 text-primary-600 hover:bg-primary-50"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Tout marquer lu
            </button>
            <button
              onClick={handlePurgeRead}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Supprimer les lues
            </button>
          </div>
        </div>

        {/* Liste */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Titre
                </th>
                <th className="hidden md:table-cell px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Module
                </th>
                <th className="hidden lg:table-cell px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Priorité
                </th>
                <th className="hidden xl:table-cell px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Reçue
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucune notification ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                items.map((n) => {
                  const style = getNotificationTypeStyle(n.type);
                  const Icon = style.icon;
                  return (
                    <tr
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`cursor-pointer ${n.is_read ? 'bg-white' : 'bg-blue-50/40'} hover:bg-gray-50`}
                    >
                      <td className="px-4 py-3">
                        <Icon className={`w-4 h-4 ${style.className}`} />
                      </td>
                      <td className="px-4 py-3 max-w-md">
                        <p
                          className={`text-sm leading-tight ${n.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}
                        >
                          {n.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{n.message}</p>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-xs text-gray-700">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {n.module}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-xs">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${getPriorityBadgeClass(n.priority)}`}
                        >
                          {getPriorityLabel(n.priority)}
                        </span>
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-xs text-gray-500">
                        {relativeTime(n.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (n.is_read) markUnread(n.id);
                            else markRead(n.id);
                            loadStats();
                          }}
                          className="inline-flex items-center p-1.5 rounded text-gray-600 hover:bg-gray-100"
                          title={n.is_read ? 'Marquer non lue' : 'Marquer lue'}
                          aria-label={n.is_read ? 'Marquer non lue' : 'Marquer lue'}
                        >
                          <Check
                            className={`w-4 h-4 ${n.is_read ? 'text-gray-400' : 'text-green-600'}`}
                          />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dismiss(n.id);
                            loadStats();
                          }}
                          className="inline-flex items-center p-1.5 rounded text-red-600 hover:bg-red-50"
                          title="Supprimer"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-600">
              Page {pagination.page} / {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
              </button>
              <button
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'default' | 'primary' | 'blue' | 'red';
}) {
  const tones: Record<typeof tone, string> = {
    default: 'bg-white text-gray-900',
    primary: 'bg-primary-50 text-primary-700',
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className={`rounded-xl border border-gray-200 p-4 shadow-sm ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
